import { customElement, property } from "lit/decorators.js";
import {
  DataTexture,
  EquirectangularReflectionMapping,
  Matrix4,
  RGBAFormat,
  SRGBColorSpace,
  UnsignedByteType,
  OrthographicCamera,
  PerspectiveCamera,
  WebGLRenderer,
  type Camera,
  type Scene,
  type Texture,
  type ToneMapping,
  type WebGLRendererParameters,
} from "three";
import { WebGLPathTracer } from "three-gpu-pathtracer";
import {
  expandMultiMaterialMeshesForPathTrace,
  hidePathTraceIncompatible,
} from "./pathTraceScenePrep";
import { SugarElement } from "../viewport/SugarCanvas";
import { Engine, Entity } from "../../engine/Engine";
import {
  getViewerRuntimeScope,
  type PathTraceState,
  type ViewerRuntimeScope,
} from "../events/messaging/ViewerEventPlatform";
import type { Disposable } from "../events/messaging/index";
import { logger } from "../../util/Logger";
import { renderSettingsStore } from "./RenderSettingsStore";

export type PathTraceOptions = {
  renderScale: number;
  environmentIntensity: number;
  exposure: number;
  bounces: number;
  samplesPerFrame: number;
  filterGlossyFactor: number;
  fadeDuration: number;
  frameCount: number;
};

const defaultOptions: PathTraceOptions = {
  renderScale: 0.75,
  environmentIntensity: 1,
  exposure: 1,
  bounces: 4,
  samplesPerFrame: 1,
  filterGlossyFactor: 0.5,
  fadeDuration: 250,
  frameCount: 128,
};

function canvasToJpegDataUrl(
  source: HTMLCanvasElement,
  maxEdge = 1600,
  quality = 0.86,
): string | null {
  const srcW = source.width;
  const srcH = source.height;
  if (srcW < 2 || srcH < 2) return null;
  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#101010";
  ctx.fillRect(0, 0, w, h);
  try {
    ctx.drawImage(source, 0, 0, w, h);
  } catch {
    return null;
  }
  try {
    return out.toDataURL("image/jpeg", quality);
  } catch {
    return null;
  }
}

function hasEnvMapPixelData(texture: Texture): boolean {
  const image = texture.image as
    | { data?: ArrayLike<number>; width?: number; height?: number }
    | undefined;
  return !!(image?.data && image.width && image.height);
}

/**
 * three-gpu-pathtracer CDF bake needs equirect DataTexture (`image.data`).
 * PNG/JPG from TextureLoader only have HTMLImageElement — convert via 2D canvas.
 */
function equirectToDataTexture(source: Texture): DataTexture {
  const image = source.image as
    | HTMLImageElement
    | HTMLCanvasElement
    | ImageBitmap
    | OffscreenCanvas
    | { data: ArrayLike<number>; width: number; height: number }
    | undefined;

  if (!image) {
    throw new Error("Path tracing: environment map has no image.");
  }

  if ("data" in image && image.data && image.width && image.height) {
    const raw = image.data;
    const array =
      raw instanceof Uint8ClampedArray
        ? new Uint8Array(raw.buffer.slice(0))
        : (raw as ArrayLike<number>);
    const dataTex = new DataTexture(
      array as Float32Array | Uint8Array | Uint16Array,
      image.width,
      image.height,
      RGBAFormat,
      (source as DataTexture).type ?? UnsignedByteType,
    );
    dataTex.mapping = EquirectangularReflectionMapping;
    dataTex.colorSpace = source.colorSpace;
    dataTex.flipY = source.flipY;
    dataTex.needsUpdate = true;
    return dataTex;
  }

  const width = (image as CanvasImageSource & { width: number }).width;
  const height = (image as CanvasImageSource & { height: number }).height;
  if (!width || !height) {
    throw new Error("Path tracing: environment map has no dimensions.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("Path tracing: cannot read environment map pixels.");
  }
  ctx.drawImage(image as CanvasImageSource, 0, 0);
  const { data } = ctx.getImageData(0, 0, width, height);
  const dataTex = new DataTexture(
    new Uint8Array(data.buffer.slice(0)),
    width,
    height,
    RGBAFormat,
    UnsignedByteType,
  );
  dataTex.mapping = EquirectangularReflectionMapping;
  dataTex.colorSpace = source.colorSpace;
  dataTex.flipY = source.flipY;
  dataTex.needsUpdate = true;
  return dataTex;
}

/** Install a pathtracer-safe DataTexture as scene.environment while tracing. */
function installPathTraceEnvironment(scene: Scene): {
  original: Texture | null;
  converted?: DataTexture;
} {
  const original = (scene.environment as Texture | null) ?? null;
  const env = original as (Texture & { isCubeTexture?: boolean }) | null;
  if (!env || env.isCubeTexture || hasEnvMapPixelData(env)) {
    return { original };
  }
  const converted = equirectToDataTexture(env);
  scene.environment = converted;
  return { original, converted };
}

/**
 * Path tracing via three-gpu-pathtracer (WebGL2).
 * On WebGPU hosts, creates a sidecar WebGL2 canvas over the viewport.
 */
class SugarPathTracerEntity extends Entity {
  tracer?: WebGLPathTracer;
  pathTracingEnabled = false;
  options = { ...defaultOptions };
  private userPaused = false;
  private preparing = false;

  pause(): void {
    this.userPaused = true;
    if (this.animationFrame) {
      clearTimeout(this.animationFrame);
      this.animationFrame = 0;
    }
  }

  syncLights(): void {
    const scene = this.engine?.activeScene;
    if (!this.tracer || !scene) return;
    scene.updateMatrixWorld(true);
    this.tracer.updateLights();
    this.frameIndex = 0;
    this.host.emitSamples(0);
    if (this.pathTracingEnabled && !this.userPaused) this.requestFrame();
  }

  resume(): void {
    if (!this.pathTracingEnabled || !this.tracer || this.preparing) return;
    this.userPaused = false;
    this.requestFrame();
  }
  private generation = 0;
  private renderer?: WebGLRenderer;
  private ownsRenderer = false;
  private sidecarCanvas?: HTMLCanvasElement;
  private exposureBefore = 1;
  private environmentIntensityBefore = 1;
  private environmentBefore: Texture | null = null;
  private pathTraceEnvironment?: DataTexture;
  private mainCanvasOpacity: string | null = null;
  private animationFrame = 0;
  private hasRasterSnapshot = false;
  private readonly cameraWorld = new Matrix4();
  private readonly cameraProjection = new Matrix4();
  private cameraSynced = false;
  private sceneMutateTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEnvIntensity?: number;
  private runtime?: ViewerRuntimeScope;
  private listenersBound = false;
  private readonly portDisposers: Disposable[] = [];
  private frameIndex = 0;
  /** After bake, refuse setCamera/reset until the Render button turns us off. */
  private accumulateLocked = false;
  /** Frozen pose for the tracer — live orbit noise must not reset accumulate. */
  private tracerCamera?: Camera;

  constructor(private readonly host: SugarPathTracer) {
    super();
  }

  override onAdd(engine: Engine): void {
    this.runtime = getViewerRuntimeScope(engine);
    this.bindRuntimeListeners();
    // Host may already have enabled=true from settings apply before onAdd.
    if (this.host.enabled || this.pathTracingEnabled) {
      void this.setEnabled(true);
    }
  }

  override onRemove(_engine: Engine): void {
    this.pathTracingEnabled = false;
    this.accumulateLocked = false;
    this.generation++;
    this.portDisposers.splice(0).forEach((d) => d.dispose());
    this.listenersBound = false;
    this.tracerCamera = undefined;
    this.lastLiveCamera = undefined;
    if (this.sceneMutateTimer) {
      clearTimeout(this.sceneMutateTimer);
      this.sceneMutateTimer = null;
    }
    this.restoreRasterState();
    this.updateState({
      status: "inactive",
      progress: 0,
      samples: 0,
      error: undefined,
    });
    this.disposeTracer();
    this.disposeSidecar();
    this.runtime = undefined;
  }

  async setEnabled(enabled: boolean): Promise<void> {
    if (!enabled) {
      if (!this.pathTracingEnabled) return;
      this.pathTracingEnabled = false;
      this.accumulateLocked = false;
      this.generation++;
      this.restoreRasterState();
      this.updateState({
        status: "inactive",
        progress: 0,
        samples: 0,
        error: undefined,
      });
      this.host.emitState("inactive");
      return;
    }

    const engine = this.engine;
    if (!engine) {
      // Keep pathTracingEnabled false so onAdd can retry via host.enabled.
      logger.warn("[path-trace] setEnabled(true) before engine; waiting for onAdd.");
      return;
    }

    if (this.pathTracingEnabled) return;
    this.pathTracingEnabled = true;
    this.accumulateLocked = false;
    this.frameIndex = 0;

    try {
      const gl = this.ensureGlRenderer(engine);
      if (!gl) {
        this.pathTracingEnabled = false;
        const msg = "Path tracing requires WebGL2 (sidecar failed).";
        logger.warn("[path-trace]", msg);
        this.updateState({ status: "error", error: msg });
        this.host.emitError(msg);
        return;
      }
      this.renderer = gl;
      if (!this.tracer) {
        this.tracer = new WebGLPathTracer(gl);
      }
      this.bindRuntimeListeners();
      await this.prepareScene();
    } catch (error) {
      this.pathTracingEnabled = false;
      this.restoreRasterState();
      const message = error instanceof Error ? error.message : String(error);
      logger.warn("[path-trace] enable failed:", message);
      this.updateState({ status: "error", error: message });
      this.host.emitError(message);
    }
  }

  updateOptions(options: PathTraceOptions): void {
    const prev = this.options;
    this.options = { ...options };
    if (!this.pathTracingEnabled) return;
    const needsReset =
      prev.renderScale !== options.renderScale ||
      prev.bounces !== options.bounces ||
      prev.samplesPerFrame !== options.samplesPerFrame ||
      prev.filterGlossyFactor !== options.filterGlossyFactor ||
      prev.fadeDuration !== options.fadeDuration;
    const unchanged =
      !needsReset &&
      prev.exposure === options.exposure &&
      prev.environmentIntensity === options.environmentIntensity &&
      prev.frameCount === options.frameCount;
    if (unchanged) return;
    this.applyOptions(needsReset);
  }

  reset(): void {
    if (this.pathTracingEnabled) return;
    this.frameIndex = 0;
    this.tracer?.reset();
    this.requestFrame();
  }

  private bindRuntimeListeners(): void {
    if (this.listenersBound || !this.runtime) return;
    this.listenersBound = true;
    this.portDisposers.push(
      this.runtime.render.events.on("resized", this.onResize),
      this.runtime.camera.events.on("changed", this.onCameraReplaced),
      this.runtime.camera.events.on("interaction", this.onCameraInteraction),
      this.runtime.render.events.on("sceneMutated", this.onSceneMutated),
      this.runtime.render.commands.register(
        "resetPathTracing",
        () => this.reset(),
      ),
    );
  }

  private setMainCanvasHidden(hidden: boolean): void {
    if (!this.ownsRenderer) return;
    const canvas = this.engine?.canvas;
    if (!canvas) return;
    // Keep the WebGPU canvas hittable (OrbitControls binds to it). opacity:0
    // hides pixels; visibility:hidden would drop pointer events.
    if (hidden) {
      if (this.mainCanvasOpacity == null) {
        this.mainCanvasOpacity = canvas.style.opacity || "";
      }
      canvas.style.opacity = "0";
    } else if (this.mainCanvasOpacity != null) {
      canvas.style.opacity = this.mainCanvasOpacity;
      this.mainCanvasOpacity = null;
    }
  }

  /** Push live engine camera into the pathtracer when matrices change. */
  private syncTracerCamera(force = false): boolean {
    if (this.accumulateLocked) return false;
    const tracer = this.tracer;
    const camera = this.engine?.activeCamera?.instance;
    if (!tracer || !camera) return false;

    camera.updateMatrixWorld();

    const moved =
      force ||
      !this.cameraSynced ||
      !this.cameraWorld.equals(camera.matrixWorld) ||
      !this.cameraProjection.equals(camera.projectionMatrix);

    if (!moved) return false;

    this.cameraWorld.copy(camera.matrixWorld);
    this.cameraProjection.copy(camera.projectionMatrix);
    this.cameraSynced = true;
    // Snapshot — three-gpu-pathtracer resets accumulate if the live camera jitters.
    this.ensureTracerCamera(camera);
    tracer.setCamera(this.tracerCamera!);
    return true;
  }

  private ensureTracerCamera(live: Camera): Camera {
    if (!this.tracerCamera || this.tracerCamera.type !== live.type) {
      // Do not clone the live camera — Object3D.copy JSON.stringifies
      // userData, and OrbitControls stores a circular controller↔camera link.
      this.tracerCamera =
        live instanceof OrthographicCamera
          ? new OrthographicCamera()
          : new PerspectiveCamera();
    }
    const dest = this.tracerCamera;
    dest.position.copy(live.position);
    dest.quaternion.copy(live.quaternion);
    dest.scale.copy(live.scale);
    dest.matrixWorld.copy(live.matrixWorld);
    dest.matrixWorldInverse.copy(live.matrixWorldInverse);
    dest.projectionMatrix.copy(live.projectionMatrix);
    dest.projectionMatrixInverse.copy(live.projectionMatrixInverse);
    const src = live as Camera & {
      fov?: number;
      aspect?: number;
      zoom?: number;
      near?: number;
      far?: number;
      left?: number;
      right?: number;
      top?: number;
      bottom?: number;
    };
    const out = dest as typeof src;
    if (src.fov != null) out.fov = src.fov;
    if (src.aspect != null) out.aspect = src.aspect;
    if (src.zoom != null) out.zoom = src.zoom;
    if (src.near != null) out.near = src.near;
    if (src.far != null) out.far = src.far;
    if (src.left != null) {
      out.left = src.left;
      out.right = src.right;
      out.top = src.top;
      out.bottom = src.bottom;
    }
    dest.updateMatrixWorld(true);
    return dest;
  }

  private ensureGlRenderer(engine: Engine): WebGLRenderer | undefined {
    if (this.renderer) return this.renderer;

    const main = engine.renderer.instance;
    if (main instanceof WebGLRenderer && main.capabilities.isWebGL2) {
      this.ownsRenderer = false;
      logger.warn("[path-trace] using main WebGL2 renderer");
      return main;
    }

    const mount =
      engine.canvas?.parentElement ??
      engine.bindedElement ??
      null;
    if (!mount) {
      logger.warn(
        "[path-trace] no mount parent (canvas.parent / bindedElement)",
      );
      return undefined;
    }

    const canvas = document.createElement("canvas");
    canvas.className = "sugar-path-trace-canvas";
    Object.assign(canvas.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      display: "none",
      zIndex: "5",
      pointerEvents: "none",
    } as Partial<CSSStyleDeclaration>);

    const prevPosition = getComputedStyle(mount).position;
    if (prevPosition === "static") {
      mount.style.position = "relative";
    }
    mount.appendChild(canvas);
    this.sidecarCanvas = canvas;

    let gl: WebGLRenderer;
    try {
      const params: WebGLRendererParameters = {
        canvas,
        antialias: false,
        alpha: true,
        powerPreference: "high-performance",
        preserveDrawingBuffer: true,
      };
      gl = new WebGLRenderer(params);
    } catch (error) {
      canvas.remove();
      this.sidecarCanvas = undefined;
      logger.warn("[path-trace] WebGLRenderer create failed", error);
      return undefined;
    }

    if (!gl.capabilities.isWebGL2) {
      gl.dispose();
      canvas.remove();
      this.sidecarCanvas = undefined;
      logger.warn("[path-trace] WebGL2 not available for sidecar");
      return undefined;
    }

    const w = Math.max(
      1,
      engine.width || mount.clientWidth || engine.canvas?.clientWidth || 1,
    );
    const h = Math.max(
      1,
      engine.height || mount.clientHeight || engine.canvas?.clientHeight || 1,
    );
    gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    gl.setSize(w, h, false);
    gl.outputColorSpace = SRGBColorSpace;
    const mainTone = (main as { toneMapping?: ToneMapping }).toneMapping;
    const mainExposure = (main as { toneMappingExposure?: number })
      .toneMappingExposure;
    if (mainTone != null) gl.toneMapping = mainTone;
    if (mainExposure != null) gl.toneMappingExposure = mainExposure;

    this.ownsRenderer = true;
    // warn level so it shows with default Logger threshold
    logger.warn(
      `[path-trace] WebGL2 sidecar ready (${w}x${h}) on`,
      mount.tagName,
    );
    return gl;
  }

  private disposeTracer(): void {
    this.tracer?.dispose();
    this.tracer = undefined;
  }

  private disposeSidecar(): void {
    this.setSidecarVisible(false);
    if (this.ownsRenderer && this.renderer) {
      this.renderer.dispose();
      this.sidecarCanvas?.remove();
      this.sidecarCanvas = undefined;
      this.renderer = undefined;
      this.ownsRenderer = false;
    } else {
      this.renderer = undefined;
    }
  }

  private setSidecarVisible(visible: boolean): void {
    if (!this.sidecarCanvas) return;
    this.sidecarCanvas.style.display = visible ? "block" : "none";
  }

  private async prepareScene(): Promise<void> {
    if (
      !this.pathTracingEnabled ||
      this.preparing ||
      !this.tracer ||
      !this.engine?.activeScene ||
      !this.engine.activeCamera
    ) {
      logger.warn("[path-trace] prepareScene skipped", {
        enabled: this.pathTracingEnabled,
        preparing: this.preparing,
        hasTracer: !!this.tracer,
        hasScene: !!this.engine?.activeScene,
        hasCamera: !!this.engine?.activeCamera,
      });
      return;
    }

    const generation = ++this.generation;
    this.preparing = true;
    this.updateState({
      status: "preparing",
      progress: 0,
      samples: 0,
      error: undefined,
    });
    this.host.emitState("preparing");

    try {
      this.captureRasterState();
      this.syncSidecarSize();
      this.setSidecarVisible(true);
      this.setMainCanvasHidden(true);
      this.host.emitProgress(0);

      const scene = this.engine.activeScene;
      const camera = this.engine.activeCamera.instance;
      // Walls use material[]; pathtracer assumes 1 material/mesh — expand first.
      const expand = expandMultiMaterialMeshesForPathTrace(scene);
      const { meshCount, restore: restoreHidden } =
        hidePathTraceIncompatible(scene);
      const restore = () => {
        restoreHidden();
        expand.restore();
      };
      try {
        if (meshCount === 0) {
          logger.warn("[path-trace] waiting for compatible meshes…");
          this.setSidecarVisible(false);
          this.setMainCanvasHidden(false);
          this.engine.setRenderOverride(undefined);
          this.updateState({ status: "preparing", progress: 0 });
          return;
        }
        logger.warn(`[path-trace] baking ${meshCount} mesh(es)…`);
        this.installEnvironmentForPathTrace(scene);
        const tracerCam = this.ensureTracerCamera(camera);
        await Promise.resolve(
          this.tracer!.setScene(scene, tracerCam, {
            onProgress: (progress) => {
              this.updateState({ progress });
              this.host.emitProgress(progress);
            },
          }),
        );
      } finally {
        restore();
      }

      logger.warn(`[path-trace] baked ${meshCount} mesh(es)`);

      this.updateState({ progress: 1 });
      this.host.emitProgress(1);
      if (!this.pathTracingEnabled || generation !== this.generation) return;

      this.lastEnvIntensity = this.options.environmentIntensity;
      this.applyOptions(false);
      this.accumulateLocked = true;
      this.engine.setRenderOverride(() => {});
      this.lastLiveCamera = camera;
      this.updateState({ status: "active" });
      this.host.emitState("active");
      logger.warn("[path-trace] active, sampling…");
      this.requestFrame();
    } catch (error) {
      if (generation !== this.generation) return;
      this.pathTracingEnabled = false;
      this.restoreRasterState();
      const message = error instanceof Error ? error.message : String(error);
      logger.warn("[path-trace] prepareScene failed:", message, error);
      this.updateState({ status: "error", error: message });
      this.host.emitError(message);
    } finally {
      this.preparing = false;
    }
  }

  private installEnvironmentForPathTrace(scene: Scene): void {
    if (this.pathTraceEnvironment && scene.environment === this.pathTraceEnvironment) {
      return;
    }
    this.restoreEnvironment();
    const { original, converted } = installPathTraceEnvironment(scene);
    this.environmentBefore = original;
    this.pathTraceEnvironment = converted;
    if (converted) {
      logger.warn("[path-trace] environment converted to DataTexture for CDF bake");
    }
  }

  private restoreEnvironment(): void {
    const scene = this.engine?.activeScene;
    if (scene && this.pathTraceEnvironment && scene.environment === this.pathTraceEnvironment) {
      scene.environment = this.environmentBefore;
    } else if (scene && this.environmentBefore != null && !scene.environment) {
      scene.environment = this.environmentBefore;
    }
    this.pathTraceEnvironment?.dispose();
    this.pathTraceEnvironment = undefined;
    this.environmentBefore = null;
  }

  private captureRasterState(): void {
    if (this.hasRasterSnapshot || !this.engine?.activeScene) return;
    const main = this.engine.renderer.instance as {
      toneMappingExposure?: number;
    };
    this.exposureBefore = main.toneMappingExposure ?? 1;
    this.environmentIntensityBefore =
      this.engine.activeScene.environmentIntensity ?? 1;
    this.hasRasterSnapshot = true;
  }

  private restoreRasterState(): void {
    if (this.animationFrame) {
      clearTimeout(this.animationFrame);
      this.animationFrame = 0;
    }
    this.engine?.setRenderOverride(undefined);
    this.setSidecarVisible(false);
    this.setMainCanvasHidden(false);
    this.restoreEnvironment();
    const main = this.engine?.renderer.instance as
      | { toneMappingExposure?: number }
      | undefined;
    if (main && "toneMappingExposure" in main) {
      main.toneMappingExposure = this.exposureBefore;
    }
    if (this.renderer && this.ownsRenderer) {
      this.renderer.toneMappingExposure = this.exposureBefore;
    }
    if (this.engine?.activeScene) {
      this.engine.activeScene.environmentIntensity =
        this.environmentIntensityBefore;
    }
    this.hasRasterSnapshot = false;
    if (this.runtime?.render.commands.has("invalidate")) {
      void this.runtime.render.commands.execute(
        "invalidate",
        "path-tracing-restored",
      );
    }
  }

  private syncSidecarSize(): void {
    if (!this.ownsRenderer || !this.renderer || !this.engine) return;
    const w = Math.max(1, this.engine.width || 1);
    const h = Math.max(1, this.engine.height || 1);
    this.renderer.setSize(w, h, false);
  }

  private applyOptions(reset: boolean): void {
    const tracer = this.tracer;
    if (!tracer) return;
    if (this.accumulateLocked) {
      tracer.bounces = this.options.bounces;
      tracer.filterGlossyFactor = this.options.filterGlossyFactor;
      if (this.renderer) {
        this.renderer.toneMappingExposure = this.options.exposure;
      }
      const scene = this.engine?.activeScene;
      if (scene && this.lastEnvIntensity !== this.options.environmentIntensity) {
        scene.environmentIntensity = this.options.environmentIntensity;
        this.lastEnvIntensity = this.options.environmentIntensity;
        tracer.updateEnvironment();
        reset = true;
      }
      if (reset) {
        this.frameIndex = 0;
        tracer.reset();
        this.host.emitSamples(0);
      }
      if (!this.userPaused) this.requestFrame();
      return;
    }
    tracer.renderScale = this.options.renderScale;
    tracer.bounces = this.options.bounces;
    tracer.filterGlossyFactor = this.options.filterGlossyFactor;
    tracer.fadeDuration = this.options.fadeDuration;
    tracer.renderDelay = 0;
    tracer.dynamicLowRes = false;
    tracer.lowResScale = Math.min(0.5, this.options.renderScale);
    if (this.renderer) {
      this.renderer.toneMappingExposure = this.options.exposure;
    }
    const scene = this.engine?.activeScene;
    if (scene) {
      const intensity = this.options.environmentIntensity;
      scene.environmentIntensity = intensity;
      if (!this.accumulateLocked && this.lastEnvIntensity !== intensity) {
        this.lastEnvIntensity = intensity;
        tracer.updateEnvironment();
        reset = true;
      }
    }
    if (reset && !this.accumulateLocked) {
      this.frameIndex = 0;
      tracer.reset();
    }
    this.requestFrame();
  }

  private readonly renderFrame = (): void => {
    this.animationFrame = 0;
    if (this.userPaused || !this.pathTracingEnabled || !this.tracer) return;
    for (let i = 0; i < this.options.samplesPerFrame; i++) {
      this.tracer.renderSample();
    }
    const traced = this.tracer.samples;
    const frames = Number.isFinite(traced) ? traced : this.frameIndex + 1;
    this.frameIndex = frames;
    this.updateState({ samples: frames });
    this.host.emitSamples(frames);
    const target = this.options.frameCount ?? 0;
    if (target > 0 && frames >= target) {
      this.completeTargetFrames(frames);
      return;
    }
    this.requestFrame();
  };

  private captureSidecarDataUrl(): string | null {
    const fromGl = this.captureGlFramebuffer();
    if (fromGl) return fromGl;
    const src = this.sidecarCanvas ?? this.renderer?.domElement;
    if (!(src instanceof HTMLCanvasElement) || src.width < 2 || src.height < 2) {
      return null;
    }
    return canvasToJpegDataUrl(src);
  }

  private captureGlFramebuffer(): string | null {
    const renderer = this.renderer;
    if (!renderer) return null;
    const canvas = renderer.domElement;
    const w = canvas.width;
    const h = canvas.height;
    if (w < 2 || h < 2) return null;
    const gl = renderer.getContext() as WebGLRenderingContext | null;
    if (!gl) return null;
    const pixels = new Uint8Array(w * h * 4);
    try {
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    } catch {
      return null;
    }
    let nonempty = false;
    for (let i = 0; i < pixels.length; i += 32) {
      if (pixels[i] || pixels[i + 1] || pixels[i + 2]) {
        nonempty = true;
        break;
      }
    }
    if (!nonempty) return null;
    const row = w * 4;
    const flipped = new Uint8ClampedArray(w * h * 4);
    for (let y = 0; y < h; y++) {
      flipped.set(pixels.subarray((h - 1 - y) * row, (h - y) * row), y * row);
    }
    const out = document.createElement("canvas");
    out.width = w;
    out.height = h;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.putImageData(new ImageData(flipped, w, h), 0, 0);
    return canvasToJpegDataUrl(out);
  }

  private completeTargetFrames(frames: number): void {
    if (!this.pathTracingEnabled) return;
    const dataUrl = this.captureSidecarDataUrl();
    this.host.emitComplete(dataUrl);
    this.pathTracingEnabled = false;
    this.accumulateLocked = false;
    this.generation++;
    this.restoreRasterState();
    this.updateState({
      status: "inactive",
      progress: 1,
      samples: frames,
      error: undefined,
    });
    this.host.emitState("inactive");
    if (renderSettingsStore.value.pathTrace.enabled) {
      renderSettingsStore.update({ pathTrace: { enabled: false } });
    }
  }

  private requestFrame(): void {
    if (this.userPaused || !this.pathTracingEnabled || this.animationFrame) return;
    // setTimeout keeps sampling when the tab is in the background; rAF pauses.
    this.animationFrame = window.setTimeout(this.renderFrame, 16);
  }

  private readonly onResize = (): void => {
    if (this.accumulateLocked) return;
    this.syncSidecarSize();
    this.syncTracerCamera(true);
    this.requestFrame();
  };

  private lastLiveCamera?: Camera;

  private readonly onCameraReplaced = (): void => {
    return;
  };

  private readonly onCameraInteraction = (event: { active: boolean }): void => {
    if (!event.active || !this.accumulateLocked || !this.tracer) return;
    this.accumulateLocked = false;
    this.syncTracerCamera(true);
    this.accumulateLocked = true;
    this.frameIndex = 0;
    this.host.emitSamples(0);
    if (!this.userPaused) this.requestFrame();
  };

  private readonly onSceneMutated = (): void => {
    if (!this.pathTracingEnabled) return;
    if (this.accumulateLocked) return;
    if (this.sceneMutateTimer) clearTimeout(this.sceneMutateTimer);
    this.sceneMutateTimer = setTimeout(() => {
      this.sceneMutateTimer = null;
      if (!this.pathTracingEnabled || this.accumulateLocked) return;
      if (this.tracer && !this.preparing) return;
      void this.prepareScene();
    }, 250);
  };

  private updateState(patch: Partial<PathTraceState>): void {
    this.runtime?.pathTracing.update((state) => ({ ...state, ...patch }));
  }
}

@customElement("sugar-path-tracer")
export class SugarPathTracer extends SugarElement {
  @property({ type: Boolean }) enabled = false;
  @property({ attribute: false }) options: PathTraceOptions = {
    ...defaultOptions,
  };

  override createEntity(): Entity {
    return new SugarPathTracerEntity(this);
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    const entity = this.entity as SugarPathTracerEntity | undefined;
    if (changed.has("enabled")) void entity?.setEnabled(this.enabled);
    if (changed.has("options")) entity?.updateOptions(this.options);
  }

  resetAccumulation(): void {
    return;
  }

  emitState(state: "inactive" | "preparing" | "active"): void {
    this.dispatchEvent(
      new CustomEvent("path-trace-state", {
        detail: { state },
        bubbles: true,
        composed: true,
      }),
    );
  }

  emitProgress(progress: number): void {
    this.dispatchEvent(
      new CustomEvent("path-trace-progress", {
        detail: { progress },
        bubbles: true,
        composed: true,
      }),
    );
  }

  emitSamples(samples: number): void {
    this.dispatchEvent(
      new CustomEvent("path-trace-samples", {
        detail: { samples },
        bubbles: true,
        composed: true,
      }),
    );
  }

  emitError(message: string): void {
    this.dispatchEvent(
      new CustomEvent("path-trace-error", {
        detail: { message },
        bubbles: true,
        composed: true,
      }),
    );
  }

  emitComplete(dataUrl: string | null): void {
    this.dispatchEvent(
      new CustomEvent("path-trace-complete", {
        detail: { dataUrl },
        bubbles: true,
        composed: true,
      }),
    );
  }

  pausePathTrace(): void {
    (this.entity as SugarPathTracerEntity | undefined)?.pause();
  }

  resumePathTrace(): void {
    const entity = this.entity as SugarPathTracerEntity | undefined;
    entity?.syncLights();
    entity?.resume();
  }

  syncLights(): void {
    (this.entity as SugarPathTracerEntity | undefined)?.syncLights();
  }
}

export { defaultOptions as defaultPathTraceOptions };
/** @deprecated Use SugarPathTracer */
export type SugarModelViewerPathTracer = SugarPathTracer;
